"use strict"

import React, { forwardRef } from "react"

import AbstractCanvas from "../AbstractCanvas"

class SeriesPlotCanvas extends AbstractCanvas {
  componentDidMount() {
    this.draw()
  }

  componentDidUpdate(prevProps) {
    // Not doing any checks here because this will only be invoked if
    // shouldComponentUpdate returns true

    this.draw()
  }

  render() {
    const { innerRef, width, height, margin } = this.props

    // Use passed reference (if applicable)
    if (innerRef) this.canvas = innerRef

    return (
      <div
        style={{
          position: "absolute",
          top: `${margin.top}px`,
          left: `${margin.left}px`,
        }}
      >
        <canvas
          width={width}
          height={height}
          ref={this.canvas}
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}
        />
      </div>
    )
  }

  draw() {
    const { width, height, gridlines, x, y, dataSeries } = this.props

    const g = this.getCanvasContext(width, height, this.context)
    if (!g) {
      return
    }

    if (gridlines) {
      this.gridlineLayer(g, x, y, height, width)
    }

    for (const series of dataSeries) {
      const data = series.data,
        dataStart = series.dataStart === undefined ? 0 : series.dataStart,
        dataEnd = series.dataEnd === undefined ? data.length - 1 : series.dataEnd

      if (series.style === "line" && arrayWithContents(data) && data.length > 1) {
        this.lineLayer(g, series, data, dataStart, dataEnd)
      } else if (series.style === "linespoints") {
        this.linePointLayer(g, series, data, dataStart, dataEnd)
      } else {
        this.pointLayer(g, series, data, dataStart, dataEnd)
      }

      g.restore()
    }
  }

  lineLayer(g, layer, data, dataStart, dataEnd) {
    const { x, y } = layer

    g.strokeStyle = layer.colour
    g.lineWidth = layer.strokeWidth || 3

    if (typeof layer.colour === "function") {
      let lastX, lastY

      for (let index = dataStart; index <= dataEnd; ++index) {
        const obs = data[index]

        const px = x(obs),
          py = y(obs)

        g.strokeStyle = layer.colour(obs)

        if (index > dataStart) {
          g.beginPath()
          g.moveTo(lastX, lastY)
          g.lineTo(px, py)
          g.stroke()
        }

        lastX = px
        lastY = py
      }
    } else {
      g.beginPath()
      for (let index = dataStart; index <= dataEnd; ++index) {
        const point = data[index]
        if (index === dataStart) {
          g.moveTo(x(point), y(point))
        } else {
          g.lineTo(x(point), y(point))
        }
      }
      g.stroke()
    }
  }

  linePointLayer(g, layer, data, dataStart, dataEnd) {
    const { x, y } = layer

    g.strokeStyle = layer.colour
    g.lineWidth = layer.strokeWidth || 1
    g.fillStyle = layer.colour
    const pointSize = layer.pointSize || 3

    if (typeof layer.colour === "function") {
      let lastX, lastY

      for (let index = dataStart; index <= dataEnd; ++index) {
        const obs = data[index]

        const px = x(obs),
          py = y(obs)

        g.fillStyle = g.strokeStyle = layer.colour(obs)

        g.beginPath()
        g.moveTo(px, py)
        g.arc(px, py, pointSize, 0, 2 * Math.PI)
        g.fill()

        if (index > dataStart) {
          g.beginPath()
          g.moveTo(lastX, lastY)
          g.lineTo(px, py)
          g.stroke()
        }

        lastX = px
        lastY = py
      }
    } else {
      g.beginPath()
      for (let index = dataStart; index <= dataEnd; ++index) {
        const obs = data[index]
        if (index === dataStart) {
          g.moveTo(x(obs), y(obs))
        } else {
          g.lineTo(x(obs), y(obs))
        }
      }
      g.stroke()

      g.beginPath()
      for (let index = dataStart; index <= dataEnd; ++index) {
        const obs = data[index]

        const px = x(obs),
          py = y(obs)

        g.moveTo(px, py)
        g.arc(px, py, pointSize, 0, 2 * Math.PI)
      }
      g.fill()
    }
  }

  pointLayer(g, layer, data, dataStart, dataEnd) {
    const { x, y } = layer
    const pointSize = layer.pointSize || 3

    if (typeof layer.colour === "function") {
      for (let index = dataStart; index <= dataEnd; ++index) {
        const obs = data[index]

        const px = x(obs),
          py = y(obs)

        g.fillStyle = layer.colour(obs)
        g.beginPath()
        g.moveTo(px, py)
        g.arc(px, py, pointSize, 0, 2 * Math.PI)
        g.fill()
      }
    } else {
      g.fillStyle = layer.colour

      g.beginPath()
      for (let index = dataStart; index <= dataEnd; ++index) {
        const obs = data[index]

        const px = x(obs),
          py = y(obs)

        g.moveTo(px, py)
        g.arc(px, py, pointSize, 0, 2 * Math.PI)
      }
      g.fill()
    }
  }
}

export default forwardRef((props, ref) => <SeriesPlotCanvas innerRef={ref} {...props} />)
